/* Native forms / cart / media controllers remain owned by the installed theme. */
(() => {
  'use strict';
  const productDisposers = new Map();
  let headerObserver;
  let menuAbort;
  const menuTimers = new Set();
  function setupHeader() {
    headerObserver?.disconnect();
    menuAbort?.abort();
    menuTimers.forEach(clearTimeout); menuTimers.clear();
    menuAbort = new AbortController();
    const signal = menuAbort.signal;
    const shell = document.querySelector('[data-hd-header]');
    const dialog = shell?.querySelector('.hd-mobile-menu');
    const nav = shell?.querySelector('.hd-primary-nav');
    const trigger = shell?.querySelector('[data-hd-mobile-open]');
    const mobile = matchMedia('(max-width:700px)');
    const submenus = [...(nav?.querySelectorAll('[data-hd-submenu-toggle]') || [])];
    const setSubmenu = (button, expanded) => {
      button.setAttribute('aria-expanded', String(expanded));
      const panel = document.getElementById(button.getAttribute('aria-controls'));
      if (panel) panel.hidden = !expanded;
    };
    const resetSubmenus = () => submenus.forEach(button => setSubmenu(button, false));
    // Mobile-only casing; retain original labels when returning to desktop.
    const mobileLabels = [];
    if (nav && document.documentElement.lang === 'en') {
      nav.querySelectorAll('a, summary').forEach(element => {
        element.childNodes.forEach(node => {
          if (node.nodeType !== Node.TEXT_NODE) return;
          const original = node.textContent;
          if (!/^[A-Z\s]+$/.test(original.trim())) return;
          mobileLabels.push({node, original, label:original.toLowerCase().replace(/\b[a-z]/g, letter => letter.toUpperCase())});
        });
      });
    }
    // Move the actual shortcut anchors; no duplicate links or desktop changes.
    const mobileCategories = [...(nav?.querySelectorAll('[data-hd-mobile-category]') || [])].map(link => {
      const originalLabel = link.dataset.hdCategoryOriginalLabel || link.textContent.trim();
      link.dataset.hdCategoryOriginalLabel = originalLabel;
      const mobileLabel = /^[A-Z\s]+$/.test(originalLabel) ? originalLabel.toLowerCase().replace(/^./, letter => letter.toUpperCase()) : originalLabel;
      const home = link.closest('.hd-mega-panel--shop')?.querySelector('.hd-menu-shortcuts');
      const categories = link.closest('.hd-mega-panel--shop')?.querySelector('.hd-menu-links');
      let row = link.closest('.hd-menu-column--shortcut');
      if (!row) { row = document.createElement('div'); row.className = 'hd-menu-column hd-menu-column--shortcut'; }
      return {link, home, categories, row, originalLabel, mobileLabel};
    });
    const placeCategories = () => mobileCategories.forEach(({link, home, categories, row, originalLabel, mobileLabel}) => {
      if (!home || !categories) return;
      link.textContent = mobile.matches ? mobileLabel : originalLabel;
      link.classList.toggle('hd-menu-heading', mobile.matches);
      if (mobile.matches) { row.append(link); categories.append(row); }
      else { home.append(link); row.remove(); }
    });
    submenus.forEach(button => button.addEventListener('click', () => {
      const expand = button.getAttribute('aria-expanded') !== 'true';
      submenus.forEach(other => setSubmenu(other, other === button && expand));
    }, {signal}));
    if (dialog && nav && trigger) {
      shell.classList.add('has-mobile-menu');
      const close = () => { if (dialog.open) dialog.close(); };
      const placeNavigation = () => {
        close();
        resetSubmenus();
        placeCategories();
        mobileLabels.forEach(({node, original, label}) => { node.textContent = mobile.matches ? label : original; });
        nav.querySelectorAll('details[open]').forEach(menu => { menu.open = false; });
        if (mobile.matches) dialog.querySelector('[data-hd-mobile-navigation]').append(nav);
        else shell.insertBefore(nav, dialog);
      };
      trigger.addEventListener('click', () => {
        if (!mobile.matches) return;
        dialog.showModal();
        // Show shop categories immediately on mobile rather than hiding them
        // behind an additional accordion interaction.
        const shop = nav.querySelector('.hd-mega-panel--shop')?.closest('details');
        if (shop) shop.open = true;
        trigger.setAttribute('aria-expanded', 'true');
        document.documentElement.classList.add('hd-menu-open');
      }, {signal});
      dialog.querySelector('[data-hd-mobile-close]').addEventListener('click', close, {signal});
      dialog.addEventListener('close', () => {
        resetSubmenus();
        trigger.setAttribute('aria-expanded', 'false');
        document.documentElement.classList.remove('hd-menu-open');
      }, {signal});
      dialog.addEventListener('click', event => {
        if (event.target.closest('a[href]')) close();
        if (event.target === dialog) {
          const rect = dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) close();
        }
      }, {signal});
      mobile.addEventListener('change', placeNavigation, {signal});
      placeNavigation();
    }
    document.querySelectorAll('[data-hd-menu]').forEach(menu => {
      let pending;
      const cancel = () => { clearTimeout(pending); menuTimers.delete(pending); };
      const later = (fn, delay) => { cancel(); pending = setTimeout(() => { menuTimers.delete(pending); fn(); }, delay); menuTimers.add(pending); };
      menu.addEventListener('pointerenter', event => {
        if (event.pointerType !== 'mouse' || mobile.matches || !matchMedia('(hover:hover) and (pointer:fine)').matches) return;
        later(() => { menu.open = true; }, 140);
      }, {signal});
      menu.addEventListener('pointerleave', event => {
        if (event.pointerType !== 'mouse' || mobile.matches) return;
        later(() => { if (!menu.contains(document.activeElement)) menu.open = false; }, 220);
      }, {signal});
      menu.addEventListener('click', cancel, {signal});
      menu.addEventListener('keydown', cancel, {signal});
      menu.addEventListener('focusout', () => later(() => {
        if (!menu.contains(document.activeElement) && !menu.matches(':hover')) menu.open = false;
      }, 0), {signal});
    });
    const header = document.querySelector('.layout--header-group');
    if (!header) return;
    const measure = () => {
      document.documentElement.style.setProperty('--hd-header-height', `${header.getBoundingClientRect().height}px`);
      document.dispatchEvent(new Event('henia:header-resize'));
    };
    headerObserver = new ResizeObserver(measure);
    headerObserver.observe(header);
    document.fonts?.ready.then(measure);
    measure();
  }
  document.addEventListener('toggle', event => {
    const current = event.target;
    if (!current.matches?.('[data-hd-menu]') || !current.open) return;
    document.querySelectorAll('[data-hd-menu]').forEach(menu => { if (menu !== current) menu.open = false; });
  }, true);
  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    const menu = document.querySelector('[data-hd-menu][open]');
    if (menu) { menu.open = false; menu.querySelector('summary').focus(); }
  });
  document.addEventListener('click', event => {
    if (!event.target.closest('.hd-primary-nav, [data-hd-mobile-open]')) document.querySelectorAll('[data-hd-menu][open]').forEach(menu => { menu.open = false; });
  });
  setupHeader();
  // Keep contact controls above Shopify's draft toolbar, without changing
  // the published-store position. Observe the wrapper, not its full-height iframe.
  let previewBar;
  const previewResize = new ResizeObserver(measurePreviewBar);
  function measurePreviewBar() {
    const height = previewBar?.getBoundingClientRect().height || 0;
    document.documentElement.style.setProperty('--hd-preview-bar-height', `${Math.min(height, 180)}px`);
  }
  function watchPreviewBar() {
    const next = document.getElementById('PBarNextFrameWrapper');
    if (next === previewBar) return;
    previewResize.disconnect();
    previewBar = next;
    if (next) previewResize.observe(next);
    measurePreviewBar();
  }
  new MutationObserver(watchPreviewBar).observe(document.body, {childList:true});
  watchPreviewBar();
  function setupInformation(root) {
    if (root.dataset.hdReady) return;
    root.dataset.hdReady = 'true';
    const navigation = root.querySelector('[data-hd-info-select]');
    // One set of real destinations for both desktop and phone; omit duplicates.
    if (navigation) {
      const known = new Set();
      const options = [...root.querySelectorAll('.hd-info-menu a')].filter(link => {
        if (known.has(link.href)) return false;
        known.add(link.href); return true;
      }).map(link => {
        const option = document.createElement('option');
        option.value = link.getAttribute('href');
        option.textContent = link.firstChild.textContent.trim();
        option.selected = link.hasAttribute('aria-current');
        return option;
      });
      if (!options.some(option => option.selected)) options.unshift(navigation.options[0]);
      navigation.replaceChildren(...options);
      navigation.addEventListener('change', event => { location.href = event.target.value; });
    }
    const content = root.querySelector('[data-hd-page-content]');
    const intro = root.querySelector('[data-hd-info-intro]');
    const making = root.querySelector('[data-hd-materials-image]');
    if (making && content.querySelectorAll('h2')[1]) content.querySelectorAll('h2')[1].before(making);
    // Reflow existing nodes, never replace or truncate the merchant's content.
    if (root.hasAttribute('data-hd-about') && !intro.textContent.trim()) {
      const paragraphs = [...content.children].filter(node => node.tagName === 'P' && node.textContent.trim());
      if (paragraphs.length === 6 && [...content.children].every(node => node.tagName === 'P')) {
        intro.append(paragraphs[0]);
        if (root.dataset.language === 'en') {
          const titles = ['The name behind the house.', 'From inspiration to jewelry.', 'A personal expression.'];
          [paragraphs[1], paragraphs[2], paragraphs[3]].forEach((paragraph, index) => {
            const heading = document.createElement('h2'); heading.textContent = titles[index]; paragraph.before(heading);
          });
          const quote = document.createElement('blockquote'); quote.className = 'hd-info-quote';
          quote.append(...paragraphs[4].childNodes); paragraphs[4].remove();
          paragraphs[3].previousElementSibling.before(quote);
        }
      }
    }
    const labels = new Set(['Orders','Warranty','Repairs','Cancellation Terms','Returns and Exchanges','Refund and Store Credit','Return Conditions','Jewelry That Cannot Be Returned or Exchanged','Return Process']);
    content.querySelectorAll('p').forEach(paragraph => {
      if (!labels.has(paragraph.textContent.trim())) return;
      const heading = document.createElement('h2'); heading.append(...paragraph.childNodes); paragraph.replaceWith(heading);
    });
    const toc = root.querySelector('[data-hd-info-toc]');
    content.querySelectorAll('h2,h3').forEach((heading, index) => {
      if (!heading.id) heading.id = `${root.querySelector('article').id}-heading-${index}`;
      const link = document.createElement('a'); link.href = `#${heading.id}`; link.textContent = heading.textContent; toc.append(link);
    });
    toc.hidden = !toc.children.length;
  }
  function setupCollection(root) {
    if (root.dataset.hdReady) return;
    root.dataset.hdReady = 'true';
    const panel = root.querySelector('[data-hd-filter-panel]');
    const dialog = document.createElement('dialog');
    dialog.id = panel.id; dialog.className = `${panel.className} hd`;
    dialog.setAttribute('aria-label', panel.querySelector('h2').textContent);
    dialog.append(...panel.childNodes); panel.replaceWith(dialog);
    root.querySelector('[data-hd-open-filters]').addEventListener('click', event => { event.preventDefault(); dialog.showModal(); });
    dialog.querySelector('[data-hd-close-filters]').addEventListener('click', () => dialog.close());
    root.querySelector('[data-hd-sort]').addEventListener('change', event => event.target.form.requestSubmit());
    dialog.querySelector('form').addEventListener('submit', event => {
      // Omit empty price bounds, preserving every active native filter.
      event.target.querySelectorAll('input[type=number]').forEach(input => { input.disabled = input.value === ''; });
    });
    const grid = root.querySelector('[data-hd-collection-grid]');
    const loadedPages = [];
    const entryUrl = location.href;
    let loading = false, restoring = false;
    // History is scoped to this exact entry (including locale, filters and sort).
    // Keep URLs/position, never cached product HTML, prices or availability.
    const savePosition = () => {
      if (restoring) return;
      const anchor = [...grid.children].find(card => card.getBoundingClientRect().bottom > 0);
      try {
        history.replaceState({...history.state, hdCollection:{url:entryUrl, pages:[...loadedPages], y:scrollY,
          anchor:anchor?.dataset.hdProduct, offset:anchor?.getBoundingClientRect().top}}, '', location.href);
      } catch { /* Storage restrictions must never block native navigation. */ }
    };
    root.addEventListener('click', event => {
      if (event.target.closest('[data-hd-product-link],.hd-card-title')) savePosition();
    });
    window.addEventListener('pagehide', savePosition);
    async function loadPage(more, focus = true) {
      if (loading) return false;
      loading = true; more.setAttribute('aria-busy','true');
      const status = root.querySelector('[data-hd-load-status]');
      try {
        const url = new URL(more.href, location.href);
        if (url.origin !== location.origin) throw new Error('Invalid collection URL');
        url.searchParams.set('section_id', root.dataset.sectionId);
        const response = await fetch(url);
        if (!response.ok) throw new Error('Collection could not load');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const incoming = doc.querySelector('[data-hd-collection-grid]');
        if (!incoming) throw new Error('Collection response missing');
        const known = new Set([...grid.children].map(card => card.dataset.hdProduct));
        const cards = [...incoming.children].filter(card => !known.has(card.dataset.hdProduct));
        grid.append(...cards);
        const firstLink = cards[0]?.querySelector('.hd-card-title');
        if (focus) firstLink?.focus({preventScroll:true});
        status.textContent = doc.querySelector('[data-hd-load-status]').textContent;
        const next = doc.querySelector('[data-hd-load-more]');
        loadedPages.push(more.href);
        if (next) more.href = next.href; else more.remove();
        return true;
      } catch {
        // Keep the original native pagination link available when AJAX fails.
        more.removeAttribute('data-hd-load-more');
        status.textContent = document.documentElement.lang.startsWith('he') ? 'אפשר להמשיך באמצעות הקישור שלמטה.' : 'Please use the link below to continue.';
        return false;
      } finally { loading = false; more.removeAttribute('aria-busy'); }
    }
    root.addEventListener('click', async event => {
      const more = event.target.closest('[data-hd-load-more]');
      if (!more || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      if (!restoring) await loadPage(more);
    });
    const saved = history.state?.hdCollection;
    if (saved?.url === entryUrl && performance.getEntriesByType('navigation')[0]?.type === 'back_forward') {
      restoring = true;
      history.scrollRestoration = 'manual';
      let interrupted = false;
      const cancel = () => { interrupted = true; };
      ['touchstart','wheel','keydown','pointerdown'].forEach(type => window.addEventListener(type,cancel,{once:true,passive:true}));
      (async () => {
        // Replay the native next-page chain, not arbitrary stored URLs.
        for (const page of saved.pages || []) {
          const more = root.querySelector('[data-hd-load-more]');
          if (interrupted || !more || more.href !== page || !await loadPage(more,false)) break;
        }
        await document.fonts?.ready;
        await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
        if (!interrupted) {
          const anchor = [...grid.children].find(card => card.dataset.hdProduct === saved.anchor);
          const y = anchor && Number.isFinite(saved.offset) ? scrollY + anchor.getBoundingClientRect().top - saved.offset : saved.y;
          window.scrollTo({top:Math.max(0,y || 0),behavior:'instant'});
        }
      })().finally(() => {
        restoring = false;
        history.scrollRestoration = 'auto';
        ['touchstart','wheel','keydown','pointerdown'].forEach(type => window.removeEventListener(type,cancel));
      });
    }
  }
  function scan(scope = document) {
    scope.querySelectorAll('[data-hd-policy-select]').forEach(select => {
      if (select.dataset.hdReady) return;
      select.dataset.hdReady = 'true';
      select.addEventListener('change', () => { location.href = select.value; });
    });
    scope.querySelectorAll('[data-hd-information]').forEach(setupInformation);
    scope.querySelectorAll('[data-hd-collection]').forEach(setupCollection);
    scope.querySelectorAll('[data-hd-product-page]').forEach(setupProduct);
    scope.querySelectorAll('[data-hd-related][data-url]').forEach(async root => {
      if (root.dataset.hdReady) return;
      root.dataset.hdReady = 'true';
      try {
        const response = await fetch(root.dataset.url); if (!response.ok) return;
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const cards = [...doc.querySelectorAll('[data-hd-related-grid]>.hd-product-card')];
        if (!cards.length) return;
        root.querySelector('[data-hd-related-grid]').replaceChildren(...cards); root.hidden = false;
      } catch { /* Do not invent recommendations when Shopify has none. */ }
    });
  }
  function setupProduct(root) {
    if (root.dataset.hdReady) return;
    root.dataset.hdReady = 'true';
    const gallery = root.querySelector('[data-hd-product-gallery]');
    const media = gallery.querySelector('product-media-variants');
    const form = root.querySelector('form.product-page--form');
    const primary = form.querySelector('.product-buy-buttons--primary');
    const thumbs = [...gallery.querySelectorAll('.product-media--thumb')];
    const visibleThumbs = () => thumbs.filter(thumb => getComputedStyle(thumb).display !== 'none');
    const activeMedia = () => media.querySelector('.product-media--root[data-active="true"]');
    const showMedia = id => {
      if (!id) return;
      window.dispatchEvent(new CustomEvent(`theme:product:${root.dataset.productId}:mediaChanged`, {detail:{id:Number(id)}}));
      thumbs.forEach(thumb => thumb.setAttribute('data-active', String(thumb.dataset.id === String(id))));
      updateGallery();
    };
    const fileOf = image => image ? new URL(image.currentSrc || image.src, location.href).pathname.split('/').pop() : '';
    if (root.dataset.productHandle === 'heart-medallion-necklace') {
      const rank = thumb => { const file = fileOf(thumb.querySelector('img')); return file.startsWith('HEART') ? 0 : file.includes('8996') ? 1 : file.startsWith('ARID') ? 2 : 3; };
      thumbs.sort((a,b) => rank(a)-rank(b));
      thumbs.forEach(thumb => thumb.parentElement.append(thumb));
    }
    let pairs = [];
    const pairData = root.querySelector('[data-hd-gallery-pairs]');
    if (pairData) {
      try {
        pairs = JSON.parse(pairData.textContent).files.map(files => files.map(file => thumbs.find(thumb => fileOf(thumb.querySelector('img')) === file))).filter(pair => pair.every(Boolean));
      } catch { /* No verified pair: do not present a reverse-photo promise. */ }
    }
    thumbs.forEach((thumb, index) => {
      thumb.setAttribute('role','button');
      thumb.setAttribute('aria-label', `${index + 1} — ${thumb.querySelector('img')?.alt || root.querySelector('h1').textContent}`);
      if (root.dataset.productHandle === 'heart-medallion-necklace') {
        const file = fileOf(thumb.querySelector('img'));
        const label = file.startsWith('HEART') ? 'The front' : file.startsWith('ARID') ? 'The reverse' : file.includes('8996') ? 'Full necklace' : '';
        if (label) { const span = document.createElement('span'); span.textContent = label; thumb.append(span); }
      }
    });
    const flip = gallery.querySelector('[data-hd-product-flip]');
    function currentPair() {
      const available = visibleThumbs();
      const active = activeMedia()?.dataset.id;
      return pairs.find(pair => pair.some(thumb => thumb.dataset.id === active)) || pairs.find(pair => pair.every(thumb => available.includes(thumb)));
    }
    function updateGallery() {
      const current = activeMedia(), available = visibleThumbs();
      const index = available.findIndex(thumb => thumb.dataset.id === current?.dataset.id);
      gallery.querySelector('[data-hd-gallery-count]').textContent = `${Math.max(index + 1, 1)} / ${available.length || root.querySelectorAll('.product-media--root').length}`;
      thumbs.forEach(thumb => thumb.setAttribute('aria-pressed', String(thumb.dataset.id === current?.dataset.id)));
      available.forEach((thumb, position) => thumb.setAttribute('aria-label', `${position + 1} — ${thumb.querySelector('span')?.textContent || thumb.querySelector('img')?.alt || root.querySelector('h1').textContent}`));
      const pair = currentPair();
      const note = gallery.querySelector('[data-hd-gallery-note]');
      if (note) note.textContent = pair && pair[1].dataset.id === current?.dataset.id ? 'Reverse photograph shown with example initials' : '';
      flip.hidden = !pair;
      if (pair) {
        const reversed = pair[1].dataset.id === current?.dataset.id;
        flip.setAttribute('aria-pressed', String(reversed));
        flip.firstChild.textContent = reversed ? 'See the front ' : 'Turn the medallion ';
      }
      gallery.classList.toggle('hd-full-necklace', fileOf(current?.querySelector('img')).includes('8996'));
      measure();
    }
    function move(step) {
      const available = visibleThumbs();
      if (!available.length) return;
      const index = available.findIndex(thumb => thumb.dataset.id === activeMedia()?.dataset.id);
      showMedia(available[(index + step + available.length) % available.length].dataset.id);
    }
    gallery.querySelector('[data-hd-gallery-prev]').addEventListener('click', () => move(-1));
    gallery.querySelector('[data-hd-gallery-next]').addEventListener('click', () => move(1));
    flip.addEventListener('click', () => {
      const pair = currentPair(); if (pair) showMedia(pair[activeMedia()?.dataset.id === pair[1].dataset.id ? 0 : 1].dataset.id);
    });
    function measure() {
      // Preserve the bounded, wider desktop frame; CSS contains the full photo without cropping.
      const current = activeMedia();
      const ratio = Number(current?.style.getPropertyValue('--product-media--aspect-ratio')) || 1;
      const headerHeight = document.querySelector('.layout--header-group')?.getBoundingClientRect().height || 0;
      const stage = gallery.querySelector('.product-media--container');
      const desktop = innerWidth > 700;
      const cropPortrait = desktop && current?.dataset.mediaType === 'image' && ratio < 0.9;
      const frameRatio = cropPortrait ? 0.9 : ratio;
      gallery.classList.toggle('hd-desktop-crop', cropPortrait);
      gallery.style.setProperty('--hd-stage-ratio', String(frameRatio));
      const columnWidth = gallery.getBoundingClientRect().width;
      const availableHeight = Math.max(1, innerHeight - headerHeight - 24) * (cropPortrait ? 0.94 : 1);
      const stageWidth = Math.min(columnWidth * Math.min(1, frameRatio * 1.25), availableHeight * frameRatio);
      gallery.style.setProperty('--hd-stage-width', desktop ? `${stageWidth}px` : `${Math.min(1, ratio * 1.25) * 100}%`);
      const top = desktop ? headerHeight + 16 : Math.min(headerHeight + 16, innerHeight - gallery.offsetHeight - 16);
      gallery.style.setProperty('--hd-gallery-top', `${top}px`);
      gallery.style.setProperty('--hd-stage-height', `${gallery.querySelector('.product-media--container').getBoundingClientRect().height}px`);
    }
    const resize = new ResizeObserver(measure); resize.observe(gallery);
    const mutation = new MutationObserver(() => requestAnimationFrame(updateGallery));
    mutation.observe(media, {subtree:true, attributes:true, attributeFilter:['data-active','style']});
    window.addEventListener('resize', measure, {passive:true});
    document.addEventListener('henia:header-resize', measure);
    document.fonts?.ready.then(measure);
    form.querySelectorAll('.product-letters__wrapper').forEach((wrapper, group) => {
      wrapper.querySelectorAll('select').forEach((select, index) => {
        select.id = `hd-initial-${root.dataset.productId}-${group}-${index}`;
        const initialLabel = document.documentElement.lang.startsWith('he') ? `אות ${index + 1}` : `Initial ${index + 1}`;
        select.setAttribute('aria-label', initialLabel);
        const label = document.createElement('label'); label.className = 'hd-initial-slot'; label.htmlFor = select.id;
        const title = document.createElement('span'); title.textContent = initialLabel;
        select.before(label); label.append(title, select);
      });
    });
    let appointment = form.querySelector('.open-appointment');
    if (appointment) {
      // A native link also works when the external booking popup is unavailable.
      const bookingLink = document.createElement('a');
      bookingLink.className = appointment.className;
      bookingLink.dataset.item = 'secondary-button';
      bookingLink.href = 'https://heniadanielle.setmore.com';
      bookingLink.textContent = appointment.textContent;
      appointment.replaceWith(bookingLink);
      appointment = bookingLink;
    }
    const disposers = [() => resize.disconnect(), () => mutation.disconnect(), () => window.removeEventListener('resize',measure), () => document.removeEventListener('henia:header-resize',measure)];
    // Move the existing heading, not a duplicate H1. Restore its exact desktop position.
    const mobileLayout = matchMedia('(max-width:700px)');
    // Keep the original appointment destination and desktop label intact.
    if (appointment) {
      const originalHelp = appointment.innerHTML;
      const placeHelp = () => {
        appointment.classList.toggle('hd-mobile-personal-help', mobileLayout.matches);
        appointment.innerHTML = mobileLayout.matches
          ? (document.documentElement.lang.startsWith('he')
            ? '<span>לבחירה עם ליווי אישי<small>דברו איתנו</small></span><span aria-hidden="true">←</span>'
            : '<span>Need a little guidance?<small>Speak with our team</small></span><span aria-hidden="true">→</span>')
          : originalHelp;
      };
      mobileLayout.addEventListener('change', placeHelp); placeHelp();
      disposers.push(() => { mobileLayout.removeEventListener('change', placeHelp); appointment.innerHTML = originalHelp; appointment.classList.remove('hd-mobile-personal-help'); });
    }
    // Keep the color option in its native indexed container. Only relocate price/payment content.
    const colorOption = form.querySelector('.product-options--option:has(.radios--img-swatch)');
    const priceBlock = form.querySelector('[data-block-type="price"]');
    const priceRow = priceBlock?.querySelector('.product-price--root');
    if (priceRow && !priceRow.querySelector('.hd-product-share')) {
      const he = document.documentElement.lang === 'he';
      const share = document.createElement('button');
      share.type = 'button'; share.className = 'hd-product-share';
      share.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M21 11.5 11.4 5.1v3.6C3 10.5 3 18.9 3 18.9s3.6-4.8 8.4-4.2v3.7L21 11.5Z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/></svg>';
      share.append(document.createTextNode(he ? 'שיתוף' : 'Share'));
      const panel = document.createElement('div');
      panel.className = 'hd-product-share-panel'; panel.hidden = true;
      const link = document.createElement('input');
      link.readOnly = true; link.type = 'url'; link.setAttribute('aria-label', he ? 'קישור לפריט' : 'Product link');
      const copy = document.createElement('button'); copy.type = 'button';
      copy.textContent = he ? 'העתקת קישור' : 'Copy link';
      const whatsapp = document.createElement('a');
      whatsapp.textContent = 'WhatsApp'; whatsapp.target = '_blank'; whatsapp.rel = 'noopener noreferrer';
      const status = document.createElement('span'); status.setAttribute('role', 'status');
      panel.append(link, copy, whatsapp, status);
      priceRow.append(share); priceBlock.append(panel);
      const shareData = () => {
        const url = new URL(location.pathname, location.origin);
        const variant = form.querySelector('[name="id"]')?.value || new URL(location.href).searchParams.get('variant');
        if (variant) url.searchParams.set('variant', variant);
        return {title: root.querySelector('h1')?.textContent.trim() || document.title, url: url.href};
      };
      share.addEventListener('click', async () => {
        const data = shareData();
        if (navigator.share) {
          try { await navigator.share(data); return; }
          catch (error) { if (error.name === 'AbortError') return; }
        }
        link.value = data.url;
        whatsapp.href = 'https://wa.me/?text=' + encodeURIComponent(data.title + '\n' + data.url);
        panel.hidden = !panel.hidden;
        share.setAttribute('aria-expanded', String(!panel.hidden));
        if (!panel.hidden) { link.focus(); link.select(); }
      });
      copy.addEventListener('click', async () => {
        link.value = shareData().url;
        try { await navigator.clipboard.writeText(link.value); status.textContent = he ? 'הקישור הועתק' : 'Link copied'; }
        catch { link.focus(); link.select(); status.textContent = he ? 'אפשר להעתיק את הקישור המסומן' : 'Copy the selected link'; }
      });
      disposers.push(() => { share.remove(); panel.remove(); });
    }
    if (colorOption && priceBlock) {
      const priceHome = document.createComment('hd-product-price-home');
      priceBlock.before(priceHome);
      const placePrice = () => {
        if (mobileLayout.matches) colorOption.append(priceBlock);
        else priceHome.after(priceBlock);
      };
      mobileLayout.addEventListener('change', placePrice);
      placePrice();
      disposers.push(() => {
        mobileLayout.removeEventListener('change', placePrice);
        priceHome.after(priceBlock); priceHome.remove();
      });
    }
    const title = form.querySelector('.product-page--heading');
    if (title) {
      const titleHome = document.createComment('hd-product-title-home');
      title.before(titleHome);
      const placeTitle = () => {
        title.classList.toggle('hd-mobile-product-title', mobileLayout.matches);
        if (mobileLayout.matches) gallery.before(title);
        else titleHome.after(title);
      };
      mobileLayout.addEventListener('change', placeTitle);
      placeTitle();
      disposers.push(() => {
        mobileLayout.removeEventListener('change', placeTitle);
        titleHome.after(title); title.classList.remove('hd-mobile-product-title'); titleHome.remove();
      });
    }
    if (primary) {
      const bar = document.createElement('div'); bar.className = 'hd-mobile-purchase';
      const add = document.createElement('button'); add.type = 'button';
      bar.append(add);
      root.append(bar);
      const updatePurchase = () => {
        const headerHeight = document.querySelector('.layout--header-group')?.getBoundingClientRect().height || 0;
        const rect = primary.getBoundingClientRect();
        const viewportHeight = window.visualViewport?.height || window.innerHeight;
        const inlineVisible = rect.top >= headerHeight && rect.bottom <= viewportHeight;
        // Available before and after the inline button, never duplicated while it is fully visible.
        bar.classList.toggle('is-visible', window.innerWidth <= 700 && !inlineVisible);
        add.textContent = primary.querySelector('.product-buy-buttons--cta-text')?.textContent.trim();
        add.disabled = primary.disabled || primary.getAttribute('aria-busy') === 'true';
      };
      add.addEventListener('click', () => {
        if (!form.reportValidity()) { form.querySelector(':invalid')?.scrollIntoView({block:'center'}); return; }
        primary.click();
      });
      const purchaseObserver = new MutationObserver(updatePurchase);
      purchaseObserver.observe(form, {subtree:true, childList:true, characterData:true, attributes:true, attributeFilter:['disabled','aria-busy']});
      window.addEventListener('scroll', updatePurchase, {passive:true});
      window.addEventListener('resize', updatePurchase, {passive:true}); updatePurchase();
      window.visualViewport?.addEventListener('resize', updatePurchase, {passive:true});
      disposers.push(() => purchaseObserver.disconnect(), () => window.removeEventListener('scroll',updatePurchase), () => window.removeEventListener('resize',updatePurchase), () => window.visualViewport?.removeEventListener('resize',updatePurchase));
    }
    updateGallery();
    productDisposers.set(root, () => disposers.forEach(dispose => dispose()));
  }
  scan();
  document.addEventListener('shopify:section:load', event => { setupHeader(); scan(event.target); });
  document.addEventListener('shopify:section:unload', event => {
    for (const [root, dispose] of productDisposers) {
      if (event.target.contains(root)) { dispose(); productDisposers.delete(root); }
    }
  });
})();
